(() => {
  const PARAM = "vs";

  const byId = (pastes) => {
    const map = new Map();
    pastes.forEach((paste) => {
      if (paste && paste.id) map.set(paste.id, paste);
    });
    return map;
  };

  const uniqueIds = (ids, catalog, max) => {
    const seen = new Set();
    const next = [];
    ids.forEach((id) => {
      if (!catalog.has(id) || seen.has(id) || next.length >= max) return;
      seen.add(id);
      next.push(id);
    });
    return next;
  };

  const readParam = (catalog, max) => {
    const raw = new URLSearchParams(window.location.search).get(PARAM);
    if (!raw) return null;
    const ids = uniqueIds(
      raw.split(",").map((part) => part.trim()),
      catalog,
      max
    );
    return ids.length ? ids : null;
  };

  const writeParam = (ids, defaults) => {
    const url = new URL(window.location.href);
    const same =
      ids.length === defaults.length && ids.every((id, i) => id === defaults[i]);
    if (same || ids.length === 0) url.searchParams.delete(PARAM);
    else url.searchParams.set(PARAM, ids.join(","));
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, "", next);
    return window.location.href;
  };

  const vsTitle = (pastes) => {
    const names = pastes.map((paste) => paste.brand).filter(Boolean);
    if (names.length === 0) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} vs. ${names[1]}`;
    return `${names.slice(0, -1).join(" vs. ")} vs. ${names[names.length - 1]}`;
  };

  const selectedPastes = (root, catalog) => {
    return [...root.querySelectorAll("[data-compare-id]:checked")]
      .map((input) => catalog.get(input.value))
      .filter(Boolean);
  };

  const setChecked = (root, ids) => {
    const want = new Set(ids);
    root.querySelectorAll("[data-compare-id]").forEach((input) => {
      input.checked = want.has(input.value);
    });
  };

  const syncLimit = (root, max) => {
    const checked = root.querySelectorAll("[data-compare-id]:checked").length;
    const atMax = checked >= max;
    root.querySelectorAll("[data-compare-id]").forEach((input) => {
      const lock = atMax && !input.checked;
      input.disabled = lock;
      const chip = input.closest(".hap-compare__chip");
      if (chip) chip.classList.toggle("is-locked", lock);
    });
    return checked;
  };

  const syncCount = (root, strings, count, max) => {
    const el = root.querySelector("[data-compare-count]");
    if (!el) return;
    el.textContent = (strings.count || "%n of %max selected")
      .replace("%n", String(count))
      .replace("%max", String(max));
  };

  const syncMatch = (root, strings, pastes) => {
    const title = vsTitle(pastes);
    const heading = root.querySelector("[data-compare-match-text]");
    if (heading) heading.textContent = title || strings.emptyMatch || "Pick two toothpastes";
    const live = root.querySelector("[data-compare-live]");
    if (live) {
      live.textContent =
        pastes.length < 2
          ? strings.needTwo || "Select at least two toothpastes to compare."
          : title;
    }
    if (title && pastes.length >= 2) {
      document.title = `${title} | Compare toothpaste | Klyyr`;
    }
  };

  const syncNeed = (root, count) => {
    const table = root.querySelector("[data-compare-table]");
    const need = root.querySelector("[data-compare-need]");
    const ready = count >= 2;
    if (table) table.hidden = !ready;
    if (need) need.hidden = ready;
  };

  const applyFilter = (root) => {
    const query = (root.querySelector("[data-compare-search]")?.value || "")
      .trim()
      .toLowerCase();
    const category = root.querySelector("[data-compare-filter].is-on")?.dataset.compareFilter || "all";
    let visible = 0;
    root.querySelectorAll(".hap-compare__chip").forEach((chip) => {
      const hay = (chip.dataset.compareSearch || "").toLowerCase();
      const cat = chip.dataset.compareCategory || "";
      const show = (!query || hay.includes(query)) && (category === "all" || cat === category);
      chip.hidden = !show;
      if (show) visible += 1;
    });
    const empty = root.querySelector("[data-compare-filter-empty]");
    if (empty) empty.hidden = visible > 0;
  };

  const currentIds = (root) =>
    [...root.querySelectorAll("[data-compare-id]:checked")].map((input) => input.value);

  const init = (root) => {
    if (root.dataset.compareReady === "true") return;
    const node = root.querySelector("[data-compare-config]");
    if (!node) return;

    let config;
    try {
      config = JSON.parse(node.textContent);
    } catch (err) {
      return;
    }

    const catalog = byId(config.pastes || []);
    const max = Number(config.max) || 4;
    const strings = config.strings || {};
    const defaults = uniqueIds(config.defaultIds || [], catalog, max);
    const fromUrl = readParam(catalog, max);
    const start = fromUrl || defaults;

    if (start.length) setChecked(root, start);

    const refresh = (write) => {
      const ids = currentIds(root);
      const pastes = ids.map((id) => catalog.get(id)).filter(Boolean);
      const count = syncLimit(root, max);
      syncCount(root, strings, count, max);
      syncMatch(root, strings, pastes);
      syncNeed(root, count);
      if (write !== false) writeParam(ids, defaults);
    };

    root.addEventListener("pointerdown", (event) => {
      if (event.target.closest("[data-compare-id]")) {
        root.dataset.comparePointer = "true";
      }
    });

    root.addEventListener("keydown", (event) => {
      if (event.target.closest("[data-compare-id]")) {
        delete root.dataset.comparePointer;
      }
    });

    root.addEventListener("change", (event) => {
      const input = event.target.closest("[data-compare-id]");
      if (!input) return;
      if (input.checked && currentIds(root).length > max) {
        input.checked = false;
        return;
      }
      refresh(true);
      if (root.dataset.comparePointer === "true") {
        input.blur();
        delete root.dataset.comparePointer;
      }
    });

    root.querySelectorAll("[data-compare-preset]").forEach((button) => {
      button.addEventListener("click", () => {
        const ids = uniqueIds(
          (button.dataset.comparePreset || "").split(","),
          catalog,
          max
        );
        if (!ids.length) return;
        setChecked(root, ids);
        refresh(true);
        root.querySelector("[data-compare-table]")?.scrollIntoView({ block: "nearest" });
      });
    });

    const reset = root.querySelector("[data-compare-reset]");
    if (reset) {
      reset.addEventListener("click", () => {
        setChecked(root, defaults);
        const search = root.querySelector("[data-compare-search]");
        if (search) search.value = "";
        root.querySelectorAll("[data-compare-filter]").forEach((btn) => {
          const on = btn.dataset.compareFilter === "all";
          btn.classList.toggle("is-on", on);
          btn.setAttribute("aria-pressed", on ? "true" : "false");
        });
        applyFilter(root);
        refresh(true);
      });
    }

    const share = root.querySelector("[data-compare-share]");
    const shareLabel = root.querySelector("[data-compare-share-label]");
    if (share) {
      share.addEventListener("click", async () => {
        const href = writeParam(currentIds(root), defaults);
        const original = strings.share || "Copy link";
        const copied = strings.copied || "Copied";
        const failed = strings.copyFailed || "Copy failed";
        try {
          await navigator.clipboard.writeText(href);
          if (shareLabel) shareLabel.textContent = copied;
          window.setTimeout(() => {
            if (shareLabel) shareLabel.textContent = original;
          }, 1600);
        } catch (err) {
          if (shareLabel) shareLabel.textContent = failed;
        }
      });
    }

    const search = root.querySelector("[data-compare-search]");
    if (search) {
      search.addEventListener("input", () => applyFilter(root));
    }

    root.querySelectorAll("[data-compare-filter]").forEach((button) => {
      button.addEventListener("click", () => {
        root.querySelectorAll("[data-compare-filter]").forEach((btn) => {
          const on = btn === button;
          btn.classList.toggle("is-on", on);
          btn.setAttribute("aria-pressed", on ? "true" : "false");
        });
        applyFilter(root);
      });
    });

    applyFilter(root);
    refresh(false);
    root.dataset.compareReady = "true";
  };

  const start = (scope) => {
    (scope || document).querySelectorAll("[data-toothpaste-compare]").forEach(init);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => start(), { once: true });
  } else {
    start();
  }

  document.addEventListener("shopify:section:load", (event) => start(event.target));
})();
